import React from "react";
import "./chartControls.css";

export type ChartMetric = "count" | "amount";

export type ChartGranularity = "by_week" | "by_month" | "by_year";

export interface SegmentOption<T extends string> {
  value: T;
  label: string;
  title?: string;
}

const SegmentedControl = <T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  disabled = false,
}: {
  value: T;
  options: SegmentOption<T>[];
  onChange: (value: T) => void;
  ariaLabel: string;
  disabled?: boolean;
}) => {
  const index = Math.max(
    0,
    options.findIndex((option) => option.value === value)
  );
  return (
    <div
      className="chart-segmented"
      role="group"
      aria-label={ariaLabel}
      style={{ "--chart-segments": options.length, "--chart-segment-index": index } as React.CSSProperties}
    >
      <span className="chart-segmented-thumb" aria-hidden="true" />
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? "active" : ""}
          aria-pressed={option.value === value}
          title={option.title}
          onClick={() => onChange(option.value)}
          disabled={disabled}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
};

export const METRIC_OPTIONS: SegmentOption<ChartMetric>[] = [
  { value: "count", label: "Count", title: "Show the number of records" },
  { value: "amount", label: "Value", title: "Show the total worth" },
];

export const MetricToggle = ({
  value,
  onChange,
  disabled = false,
  label,
}: {
  value: ChartMetric;
  onChange: (metric: ChartMetric) => void;
  disabled?: boolean;
  label?: string;
}) => {
  const control = (
    <SegmentedControl
      value={value}
      options={METRIC_OPTIONS}
      onChange={onChange}
      ariaLabel="Show chart by"
      disabled={disabled}
    />
  );
  if (!label) return control;
  return (
    <div className="chart-control">
      <span className="chart-control-label" title="Switch between the number of records and their total worth.">
        <i className="ti ti-chart-histogram" />
        {label}
      </span>
      {control}
    </div>
  );
};

export const GRANULARITY_OPTIONS: SegmentOption<ChartGranularity>[] = [
  { value: "by_week", label: "Week", title: "One data point per week" },
  { value: "by_month", label: "Month", title: "One data point per month" },
  { value: "by_year", label: "Year", title: "One data point per year" },
];

export const GRANULARITY_NOUNS: Record<ChartGranularity, string> = {
  by_week: "week",
  by_month: "month",
  by_year: "year",
};

export const GranularityToggle = ({
  value,
  onChange,
  disabled = false,
  options = GRANULARITY_OPTIONS,
}: {
  value: ChartGranularity;
  onChange: (granularity: ChartGranularity) => void;
  disabled?: boolean;
  options?: SegmentOption<ChartGranularity>[];
}) => (
  <div className="chart-control">
    <span
      className="chart-control-label"
      title="Sets how the chart groups its data points. It does not change the date range filter."
    >
      <i className="ti ti-calendar-stats" />
      Group by
    </span>
    <SegmentedControl
      value={value}
      options={options}
      onChange={onChange}
      ariaLabel="Group chart data by"
      disabled={disabled}
    />
  </div>
);
